import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { toast } from 'react-toastify';
import { theme } from '../styles/theme';
import Table from '../components/common/Table';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { userAPI, documentAPI } from '../utils/api';
import Papa from 'papaparse';
import { Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import {
  Download,
  Edit,
  Mail,
  Send,
  Upload,
  Search,
  FileText,
  Award,
  Briefcase,
  CheckCircle,
  AlertCircle,
  Loader,
  X
} from 'react-feather';
import Modal from '../components/common/Modal';

const UsersContainer = styled.div`
  display: grid;
  gap: ${theme.spacing.lg};
  padding: 0;
  
  

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 0;
    width: 100%;
    padding: ${theme.spacing.sm};
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-end;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  flex-wrap: nowrap;
  justify-content: center;
  min-width: 100px;

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.md};
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border: 2px dashed ${theme.colors.primary};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isDragOver ? `${theme.colors.primary}10` : 'transparent'};
  
  &:hover {
    background: ${theme.colors.primary}10;
  }

  svg {
    color: ${theme.colors.primary};
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ControlsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    padding: 0 !important;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
`;

const IconButton = styled(Button)`
  min-width: 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  svg {
    width: 14px;
    height: 14px;
    transition: all 0.3s ease;
  }

  .loader {
    position: absolute;
    animation: spin 1s linear infinite;
    color: currentColor;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.round};
  font-size: 0.75rem;
  background: ${props => props.sent ? theme.colors.success + '20' : theme.colors.warning + '20'};
  color: ${props => props.sent ? theme.colors.success : theme.colors.warning};
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const SelectContainer = styled.div`
  position: relative;
  margin: 0 0px;
  margin-bottom: 1rem;
  isolation: isolate;
  z-index: 9999;
`;

const SelectWrapper = styled.div`
  margin: 0 0px;
  margin-bottom: 1rem;
  position: relative;

  .basic-select {
    position: relative;
  }

  .select__menu-portal {
    z-index: 9999;
  }

  .select__control {
    border-color: ${theme.colors.primary};
    &:hover {
      border-color: ${theme.colors.primary};
    }
    &--is-focused {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 1px ${theme.colors.primary};
    }
  }

  .select__menu {
    position: absolute;
    width: 100%;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .select__option {
    &--is-focused {
      background-color: ${theme.colors.primary}20;
    }
    &--is-selected {
      background-color: ${theme.colors.primary};
    }
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.md} 0;
  align-items: flex-end;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.md};
  }
`;

const SearchSection = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;

  .clear-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    padding: 4px;
    color: ${theme.colors.text.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }
`;

const Users = () => {
  const { sendStatus } = useParams();

  const statusOptions = [
    { value: 'ALL', label: 'All Status' },
    { value: 'complection-sent', label: 'Completion Sent' },
    { value: 'complection-remaining', label: 'Completion Pending' },
    { value: 'offer-letter-sent', label: 'Offer Letter Sent' },
    { value: 'offer-letter-remaining', label: 'Offer Letter Pending' },
  ];

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(() => {
    if (sendStatus) {
      const option = statusOptions.find(opt => opt.value === sendStatus);
      return option ? { value: sendStatus, label: option.label } : null;
    }
    return null;
  });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState({});
  const itemsPerPage = 10;
  const [loadingStates, setLoadingStates] = useState({
    offerLetter: {},
    certificate: {},
    completion: {},
    internship: {},
    sendEmail: {}
  });
  const [modalLoading, setModalLoading] = useState(false);

  const domainOptions = [
    { value: '', label: 'All Domains' },
    { value: 'Data Science - ML/AI', label: 'Data Science - ML/AI' },
    { value: 'Cloud Computing - DevOps & AWS', label: 'Cloud Computing - DevOps & AWS' },
    { value: 'Cyber Security', label: 'Cyber Security' },
    { value: 'Web Development - MERN', label: 'Web Development - MERN' },
    { value: 'Web Development - Python', label: 'Web Development - Python' },
    { value: 'Web Development - PHP', label: 'Web Development - PHP' },
    { value: 'Web Development - JAVA', label: 'Web Development - JAVA' },
    { value: 'Python + AI + Internship Program', label: '60 Days Python + AI + Internship Program' },
    { value: '21 Days AI-Powered UI/UX Design Bootcamp', label: '21 Days AI-Powered UI/UX Design Bootcamp' },
  ];
  const modalDomainOptions = [
    // { value: '', label: 'All Domains' },
    { value: 'Data Science - ML/AI', label: 'Data Science - ML/AI' },
    { value: 'Cloud Computing - DevOps & AWS', label: 'Cloud Computing - DevOps & AWS' },
    { value: 'Cyber Security', label: 'Cyber Security' },
    { value: 'Web Development - MERN', label: 'Web Development - MERN' },
    { value: 'Web Development - Python', label: 'Web Development - Python' },
    { value: 'Web Development - PHP', label: 'Web Development - PHP' },
    { value: 'Web Development - JAVA', label: 'Web Development - JAVA' },
    { value: 'Python + AI + Internship Program', label: '60 Days Python + AI + Internship Program' },
    { value: '21 Days AI-Powered UI/UX Design Bootcamp', label: '21 Days AI-Powered UI/UX Design Bootcamp' },
  ];

  const durationOptions = [
    { value: '', label: 'All Durations' },
    { value: '45', label: '45 Days' },
    { value: '60', label: '60 Days' },
    { value: '84', label: '84 Days' },
    { value: '90', label: '90 Days' },
    { value: '150', label: '150 Days' },
    { value: '180', label: '180 Days' },
  ];

  useEffect(() => {
    if (sendStatus && !selectedStatus) {
      const statusOption = statusOptions.find(opt => opt.value === sendStatus);
      if (statusOption) {
        setSelectedStatus({ value: sendStatus, label: statusOption.label });
      }
    }
  }, [sendStatus]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await userAPI.fetchUsers(
        searchTerm, 
        selectedDomain?.value || '', 
        selectedDuration?.value || '',
        selectedStatus?.value || 'ALL'
      );
      setUsers(data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, selectedDomain, selectedDuration, selectedStatus]);

  const handleDownloadTemplate = async () => {
    try {
      // First try to download the existing template from public folder
      console.log("Downloading template");
      const response = await fetch('/ws_offerLetter_Blank - Sheet.csv');
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'student_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback to generating new CSV if file not found
        const headers = 'Sr.no,Full Name,Email,Date,Duration,SendingDate,domain,cerNo,refNo\n';
        const sampleRow = '1,John Doe,john@example.com,2024-03-01,45,2024-03-02,Web Development - MERN,WS231234,REF001';
        const csvContent = headers + sampleRow;
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'student_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      console.log("Downloading templat1e");
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      toast.error('Please select a CSV file to upload.');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const cleanedData = result.data.map((item) => ({
          "Full Name": item["Full Name"]?.trim() || "",
          "Email": item["Email"]?.trim() || "",
          "Duration": item["Duration"]?.trim() || "",
          "Date": item["Date"]?.trim() || "",
          "SendingDate": item["SendingDate"]?.trim() || "",
          "domain": item["domain"]?.trim() || "",
          "cerNo": item["cerNo"]?.trim() || "",
          "refNo": item["refNo"]?.trim() || "",
        }));

        if (!cleanedData.length) {
          toast.error('No valid data found in the CSV file.');
          return;
        }

        try {
          await userAPI.uploadCsvData(cleanedData);
          toast.success('CSV data successfully uploaded!');
          fetchUsers();
        } catch (error) {
          toast.error('Failed to upload CSV data.');
        }
      },
      error: (error) => {
        toast.error('Error parsing CSV file.');
      },
    });
  };

  const setButtonLoading = (type, id, value) => {
    setLoadingStates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: value
      }
    }));
  };

  const handleGenerateOfferLetter = async (userId) => {
    setButtonLoading('offerLetter', userId, true);
    try {
      const blob = await documentAPI.generateOfferLetter(userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offer-letter-${userId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Offer letter generated successfully');
    } catch (error) {
      toast.error('Failed to generate offer letter');
    } finally {
      setButtonLoading('offerLetter', userId, false);
    }
  };

  const handleGenerateCertificate = async (userId) => {
    setButtonLoading('certificate', userId, true);
    try {
      const blob = await documentAPI.downloadCertificate(userId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${userId}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Certificate generated successfully');
    } catch (error) {
      toast.error('Failed to generate certificate');
    } finally {
      setButtonLoading('certificate', userId, false);
    }
  };

  const handleSendEmail = async (userId) => {
    setButtonLoading('sendEmail', userId, true);
    try {
      await documentAPI.sendOfferLetterByEmail(userId);
      toast.success('Email sent successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setButtonLoading('sendEmail', userId, false);
    }
  };

  const handleCompletionButton = async (userId) => {
    setButtonLoading('completion', userId, true);
    try {
      await documentAPI.generateCertificate(userId);
      toast.success('Certificate generated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to generate certificate');
    } finally {
      setButtonLoading('completion', userId, false);
    }
  };

  const handleDownloadInternshipLetter = async (userId) => {
    setButtonLoading('internship', userId, true);
    try {
      const response = await documentAPI.generateInternshipPdf(userId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Internship_Letter_${userId}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Internship letter downloaded successfully');
    } catch (error) {
      toast.error('Failed to download internship letter');
    } finally {
      setButtonLoading('internship', userId, false);
    }
  };

  const handleUpdateUser = async () => {
    setModalLoading(true);
    try {
      await userAPI.updateUser(selectedUser);
      setShowModal(false);
      toast.success('User updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      key: 'srNo',
      label: 'Sr. No.',
      width: '70px',
      render: (row, rowIndex) => {
        const index = users.findIndex(user => user.id === row.id);
        return (
          <div style={{ 
            textAlign: 'center',
            fontWeight: 500
          }}>
            {index !== -1 ? index + 1 : '-'}
          </div>
        );
      }
    },
    {
      key: 'id',
      label: 'U-ID',
      width: '100px',
      render: (row) => (
        <div style={{ fontWeight: 500 }}>
          {row.id}
        </div>
      )
    },
    { 
      key: 'fullName',
      label: 'Full Name',
      sortable: true,
      render: (row) => (
        <div style={{ 
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '200px'
        }}>
          {row.fullName}
        </div>
      )
    },
    { 
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row) => (
        <div style={{
          maxWidth: '200px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          <a href={`mailto:${row.email}`} style={{ color: theme.colors.primary }}>
            {row.email}
          </a>
        </div>
      )
    },
    { 
      key: 'domain',
      label: 'Domain',
      sortable: true,
      render: (row) => (
        <span style={{ 
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          background: `${theme.colors.primary}08`,
          borderRadius: theme.borderRadius.sm,
          color: theme.colors.primary,
          fontSize: '0.75rem',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          display: 'inline-block'
        }}>
          {row.domain}
        </span>
      )
    },
    {
      key: 'duration',
      label: 'Duration',
      sortable: true,
      render: (row) => (
        <span style={{ 
          fontWeight: 500,
          whiteSpace: 'nowrap'
        }}>
          {row.duration} days
        </span>
      )
    },
    {
      key: 'date',
      label: 'Start Date',
      sortable: true,
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {new Date(row.date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </span>
      )
    },
    {
      key: 'sendingDate',
      label: 'Sending Date',
      sortable: true,
      render: (row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {new Date(row.sendingDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </span>
      )
    },
    {
      key: 'cerNo',
      label: 'Cert. No.',
      sortable: true,
      render: (row) => (
        <code style={{ 
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          background: 'rgba(0, 0, 0, 0.04)',
          borderRadius: theme.borderRadius.sm,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'nowrap'
        }}>
          {row.cerNo}
        </code>
      )
    },
    {
      key: 'refNo',
      label: 'Ref. No.',
      sortable: true,
      render: (row) => (
        <code style={{ 
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          background: 'rgba(0, 0, 0, 0.04)',
          borderRadius: theme.borderRadius.sm,
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'nowrap'
        }}>
          {row.refNo}
        </code>
      )
    },
    // {
    //   key: 'status',
    //   label: 'Status',
    //   sortable: true,
    //   render: (row) => (
    //     <StatusBadge sent={row.sentStatus === "yes"}>
    //       {row.sentStatus === "yes" ? (
    //         <>
    //           <CheckCircle size={12} /> Sent
    //         </>
    //       ) : (
    //         <>
    //           <AlertCircle size={12} /> Pending
    //         </>
    //       )}
    //     </StatusBadge>
    //   )
    // },
    {
      key: 'offerLetterActions',
      label: 'Offer Letter',
      width: '120px',
      render: (row) => (
        <ActionButtons>
          <IconButton
            size="small"
            variant="outlined"
            onClick={() => {
              setSelectedUser(row);
              setShowModal(true);
            }}
            title="Edit User"
          >
            <Edit />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleGenerateOfferLetter(row.id)}
            title="Generate Offer Letter"
            disabled={loadingStates.offerLetter[row.id]}
          >
            {loadingStates.offerLetter[row.id] ? (
              <Loader className="loader" size={14} />
            ) : (
              <FileText />
            )}
          </IconButton>
          <IconButton
            size="small"
            color="success"
            onClick={() => handleSendEmail(row.id)}
            style={{opacity: row.sentStatus === "yes" ? 0.5 : 1}}
            disabled={loadingStates.sendEmail[row.id]}
            title="Send Email"
          >
            {loadingStates.sendEmail[row.id] ? (
              <Loader className="loader" size={14} />
            ) : (
              <Send />
            )}
          </IconButton>
        </ActionButtons>
      )
    },
    {
      key: 'internshipActions',
      label: 'Completion',
      width: '120px',
      render: (row) => (
        <ActionButtons>
      
          <IconButton
            size="small"
            variant="outlined"
            color="info"
            onClick={() => handleGenerateCertificate(row.id)}
            disabled={loadingStates.certificate[row.id]}
            title="Generate Certificate"
          >
            {loadingStates.certificate[row.id] ? (
              <Loader className="loader" size={14} />
            ) : (
              <Award />
            )}
          </IconButton>
          <IconButton
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDownloadInternshipLetter(row.id)}
            disabled={loadingStates.internship[row.id]}
            title="Download Internship Letter"
          >
            {loadingStates.internship[row.id] ? (
              <Loader className="loader" size={14} />
            ) : (
              <Briefcase />
            )}
          </IconButton>
          <IconButton
            size="small"
            color="success"
            onClick={() => handleCompletionButton(row.id)}
            style={{opacity: row.completionsSent === "yes" ? 0.5 : 1}}
            disabled={loadingStates.completion[row.id]}
            title="Send Completion"
          >
            {loadingStates.completion[row.id] ? (
              <Loader className="loader" size={14} />
            ) : (
              <Mail />
            )}
          </IconButton>
        </ActionButtons>
      )
    }
  ];

  return (
    <UsersContainer>
      <h1 className='fw-bold' style={{ 
        margin: '${theme.spacing.md} 24px',
        fontSize: 'clamp(1.5rem, 4vw, 2rem)',
        '@media (max-width: ${theme.breakpoints.md})': {
          margin: '${theme.spacing.md} 0'
        }
      }}>
        Students Management
      </h1>
      
      <ControlsSection>
        <div className='d-flex align-items-center justify-content-center gap-2 p-4 bg-white rounded' 
          style={{
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
            '@media (max-width: ${theme.breakpoints.sm})': {
              padding: '${theme.spacing.md}'
            }
          }}>
          <Button
            color="primary"
            onClick={handleDownloadTemplate}
            startIcon={<Download />}
            className='w-100 h-75 p-0'
            fullWidth
          >
            Download CSV Template
          </Button>
        </div>

        <StyledCard>
          <FileInputWrapper>
            <FileInputLabel htmlFor="file-upload" style={{
              '@media (max-width: ${theme.breakpoints.sm})': {
                flexDirection: 'column',
                textAlign: 'center',
                padding: theme.spacing.sm
              }
            }}>
              <Upload size={24} />
              <span>Click or drag CSV file to upload</span>
            </FileInputLabel>
            <FileInput
              id="file-upload"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
            />
          </FileInputWrapper>
        </StyledCard>
      </ControlsSection>
      
      <Card style={{ 
        padding: '0px', 
        overflowX: 'auto', 
        margin: '0',
        borderRadius: 0,
        boxShadow: 'none',
        backgroundColor: 'transparent',
        position: 'relative',
        zIndex: 1,
        '@media (max-width: ${theme.breakpoints.md})': {
          margin: '0 -${theme.spacing.md}'
        }
      }}>
        <FilterSection>
          <SearchSection>
            <SearchInputWrapper>
              <Input
                placeholder="Search by name, email, or phone"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                startIcon={<Search color={theme.colors.text.secondary} />}
                className='w-100'
                style={{ paddingRight: '40px' }}
              />
              {searchTerm && (
                <div 
                  className="clear-icon"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <X size={16} />
                </div>
              )}
            </SearchInputWrapper>
          </SearchSection>
          
          <SearchSection>
            <Select
              placeholder="Filter by Domain"
              value={selectedDomain}
              onChange={(option) => setSelectedDomain(option)}
              options={domainOptions}
              isClearable
              className="basic-select"
              classNamePrefix="select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: theme.colors.border,
                }),
                menu: (base) => ({
                  ...base,
                  width: '100%'
                })
              }}
            />
          </SearchSection>

          <SearchSection>
            <Select
              placeholder="Filter by Duration"
              value={selectedDuration}
              onChange={(option) => setSelectedDuration(option)}
              options={durationOptions}
              isClearable
              className="basic-select"
              classNamePrefix="select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: theme.colors.border,
                }),
                menu: (base) => ({
                  ...base,
                  width: '100%'
                })
              }}
            />
          </SearchSection>

          <SearchSection>
            <Select
              placeholder="Filter by Status"
              value={selectedStatus}
              onChange={(option) => setSelectedStatus(option)}
              options={statusOptions}
              isClearable
              className="basic-select"
              classNamePrefix="select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: theme.colors.border,
                }),
                menu: (base) => ({
                  ...base,
                  width: '100%'
                })
              }}
            />
          </SearchSection>
        </FilterSection>

        <div style={{ overflowX: 'auto', width: '100%' }}>
          <Table
            columns={columns}
            data={users}
            loading={loading}
            pageSize={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            style={{ 
              minWidth: '100%',
              '@media (max-width: ${theme.breakpoints.md})': {
                fontSize: '0.875rem'
              }
            }}
          />
        </div>
      </Card>

      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Update User"
          onSubmit={handleUpdateUser}
          loading={modalLoading}
        >
          <Input
            label="Full Name"
            value={selectedUser?.fullName || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, fullName: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Email"
            type="email"
            value={selectedUser?.email || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, email: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Duration"
            value={selectedUser?.duration || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, duration: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Date"
            type="date"
            value={selectedUser?.date || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, date: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Sending Date"
            type="date"
            value={selectedUser?.sendingDate || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, sendingDate: e.target.value }))}
            disabled={modalLoading}
          />
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 500 
            }}>
              Domain
            </label>
            <Select
              value={domainOptions.find(option => option.value === selectedUser?.domain) || null}
              onChange={(option) => setSelectedUser(prev => ({ ...prev, domain: option?.value || '' }))}
              options={modalDomainOptions}
              isClearable
              isDisabled={modalLoading}
              className="basic-select"
              classNamePrefix="select"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '42px',
                  borderColor: theme.colors.border,
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999
                })
              }}
            />
          </div>
          <Input
            label="Certificate No"
            value={selectedUser?.cerNo || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, cerNo: e.target.value }))}
            disabled={modalLoading}
          />
          <Input
            label="Reference No"
            value={selectedUser?.refNo || ''}
            onChange={(e) => setSelectedUser(prev => ({ ...prev, refNo: e.target.value }))}
            disabled={modalLoading}
          />
        </Modal>
      )}
    </UsersContainer>
  );
};

export default Users; 